import { useEffect, useState } from 'react';
import { UserCheck } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import ModalConfirmar from './ModalConfirmar';

// A quién le toca.
//
// Se usa igual desde Conteos y desde Compras a propósito: «tomar una hoja»
// y «tomar una orden» tienen que significar lo mismo para el operador, y
// dos diálogos distintos se separan a la primera que alguien arregle uno.
//
// EL DESPLEGABLE SOLO TRAE A QUIEN PUEDE HACER EL TRABAJO. La regla vive
// en el servidor —no se asigna un conteo a quien no puede contar—, pero
// una lista que ofrece a los catorce empleados y rechaza a nueve DESPUÉS
// de elegir se vive como un fallo del sistema, no como una regla. Aquí no
// se ofrece lo imposible.

/** Quién la tiene, para una columna de lista. */
export function ChipAsignado({ nombre, idAsignado }) {
  const { usuario } = useAuth();
  if (!nombre) {
    // «Sin asignar» NO es un hueco: es un estado con significado —esto se
    // puede tomar—, y un guion lo haría parecer un dato que falta.
    return <span className="text-xs text-gray-500 italic">sin asignar</span>;
  }
  const mia = idAsignado === usuario?.id;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${
      mia ? 'text-[var(--color-marca)]' : 'text-gray-700'}`}>
      <UserCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {mia ? 'Tú' : nombre}
    </span>
  );
}

/**
 * Diálogo de asignación.
 *
 * `ruta` es el endpoint (`/conteos/12/asignar`), `rolMinimo` el rol que
 * hace falta para hacer el trabajo, y `actual` quién lo tiene ahora.
 */
export default function ModalAsignar({
  ruta, rolMinimo = 'OPERADOR', actual, nombreActual, que, onCerrar, onListo,
}) {
  const { usuario, tieneNivel } = useAuth();
  const [gente, setGente] = useState([]);
  const [elegido, setElegido] = useState(actual ? String(actual) : '');
  const manda = tieneNivel('ENCARGADO');

  useEffect(() => {
    if (!manda) return;      // quien no reparte no necesita la lista
    let vivo = true;
    api.get(`/usuarios/asignables?rol_minimo=${rolMinimo}`)
      .then((g) => vivo && setGente(g))
      .catch(() => {});
    return () => { vivo = false; };
  }, [manda, rolMinimo]);

  // Sin mando solo hay dos botones posibles: tomarlo o soltarlo. Enseñarle
  // un desplegable de personas a quien no puede repartir sería ofrecerle
  // una decisión que el servidor le va a rechazar.
  const soloTomar = !manda;

  return (
    <ModalConfirmar
      titulo={soloTomar ? `Tomar ${que}` : `Asignar ${que}`}
      descripcion={
        soloTomar
          ? 'Queda a tu nombre para que nadie más lo haga a la vez. Puedes soltarlo después.'
          : 'Sale de la lista de trabajo de los demás y aparece en la de esa persona. Lo que se deja sin asignar sigue viéndose y cualquiera puede tomarlo.'
      }
      extra={
        <div className="space-y-3">
          {nombreActual && (
            <p className="text-sm text-gray-600">
              Ahora lo tiene <strong className="text-gray-900">{nombreActual}</strong>.
            </p>
          )}
          {!soloTomar && (
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">
                ¿A quién le toca?
              </span>
              <select value={elegido} onChange={(e) => setElegido(e.target.value)}
                      className="entrada">
                <option value="">— Sin asignar (que lo tome quien pueda) —</option>
                {gente.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.nombre}{g.id === usuario?.id ? ' (tú)' : ''} · {g.rol.toLowerCase()}
                  </option>
                ))}
              </select>
              {gente.length === 0 && (
                <span className="block text-xs text-amber-700 mt-1">
                  No hay nadie con permiso para hacerlo. Da de alta a alguien
                  con rol {rolMinimo.toLowerCase()} o superior.
                </span>
              )}
            </label>
          )}
        </div>
      }
      etiquetaConfirmar={soloTomar ? 'Tomarlo' : 'Guardar'}
      onConfirmar={async () => {
        const id = soloTomar ? usuario?.id : (elegido ? Number(elegido) : null);
        if (soloTomar && !id) throw new ErrorApi('No se pudo identificar tu usuario.');
        await api.post(ruta, { id_usuario: id });
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}
